import { useRef, type ReactNode } from 'react';
import { Link, Route, Switch, useLocation } from 'wouter';
import { AuthProvider } from './auth/AuthProvider';
import { AssessmentRunner } from './components/assessment/AssessmentRunner';
import { ResultsReady } from './components/assessment/ResultsReady';
import { Welcome } from './components/assessment/Welcome';
import { ResultPage } from './components/assessment/ResultView';
import { ReportPage } from './components/report/ReportPage';
import { ReportPrint } from './components/report/ReportPrint';
import { ReportLibrary } from './components/report/ReportLibrary';
import { WorkbookPage } from './components/report/WorkbookPage';
import { Bottleneck } from './components/marketing/Bottleneck';
import { Landing } from './components/marketing/Landing';
import { Perspective } from './components/marketing/Perspective';
import { WorkFit } from './components/marketing/WorkFit';
import { Why } from './components/marketing/Why';
import { AdminConsole } from './components/admin/AdminConsole';
import { RequireAdmin } from './components/admin/RequireAdmin';
import { Dashboard } from './components/dashboard/Dashboard';
import { RequireAuth } from './components/auth/RequireAuth';
import { ApplicationShell } from './components/shell/ApplicationShell';
import { ToastProvider } from './components/ui/toast';
import { ExperienceShell } from './components/shell/ExperienceShell';
import { resolveShell, resolveShellWidth } from './lib/shell/shell';
import { useRouteFocus } from './lib/shell/useRouteFocus';
import {
  PasswordResetRequest,
  PasswordUpdate,
} from './components/auth/PasswordReset';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';
import { TermsOfService } from './components/legal/TermsOfService';
import { isAssessmentType, isWorkbookSpan, routes } from './lib/assessment/routes';

/**
 * Two-shell composition (V2-018C-1).
 *
 * Shell selection is derived from the location rather than declared per route,
 * so a route added to `PUBLIC_PATHS` cannot be registered in the switch below
 * and quietly keep the wrong frame. `resolveShell` is a pure function and is
 * asserted directly in `test/shellRoutes.test.ts`.
 *
 * `Shell` owns the main-landmark ref and the route-focus behaviour, because it
 * is the only component that stays mounted across a shell change. The shells
 * receive the ref and attach it; neither knows the other exists.
 */
function Shell({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const mainRef = useRef<HTMLElement | null>(null);
  useRouteFocus(mainRef);

  return resolveShell(location) === 'public' ? (
    <ExperienceShell mainRef={mainRef}>{children}</ExperienceShell>
  ) : (
    <ApplicationShell mainRef={mainRef} width={resolveShellWidth(location)}>
      {children}
    </ApplicationShell>
  );
}

export function App() {
  return (
    <AuthProvider>
      {/* Production mounts its toaster app-wide, above the router, so a
          confirmation survives the navigation that follows it. */}
      <ToastProvider>
        <Shell>
        <Switch>
          <Route path={routes.home}>
            <Landing />
          </Route>

          <Route path={routes.why}>
            <Why />
          </Route>

          {/* The authenticated home (U1a). `RequireAuth` wraps only this
              element: a guard placed around the switch would gate the public
              website too. */}
          <Route path={routes.dashboard}>
            <RequireAuth>
              <Dashboard />
            </RequireAuth>
          </Route>

          {/* The administrative console (A4a). `RequireAdmin` composes
              `RequireAuth`, so the admin check is only ever made with a
              genuinely signed-in session.

              **Exactly one admin route, deliberately.** Production answers Not
              Found for every `/admin/*` sibling, and wouter matches exactly, so
              registering the bare path is what reproduces that: the switch's
              fallback answers `/admin/users`, `/admin/customers` and the rest
              without a rule of its own. */}
          <Route path={routes.admin}>
            <RequireAdmin>
              <AdminConsole />
            </RequireAdmin>
          </Route>

          <Route path={routes.bottleneck}>
            <Bottleneck />
          </Route>

          <Route path={routes.workFit}>
            <WorkFit />
          </Route>

          <Route path={routes.perspective}>
            <Perspective />
          </Route>

          <Route path="/assessment/:type">
            {(params) =>
              isAssessmentType(params.type) ? (
                <AssessmentRunner type={params.type} />
              ) : (
                <NotFound />
              )
            }
          </Route>

          <Route path={routes.resultsReady}>
            <ResultsReady />
          </Route>

          <Route path={routes.welcome}>
            <Welcome />
          </Route>

          <Route path="/results/:id">{(params) => <ResultPage id={params.id} />}</Route>

          <Route path={routes.reports}>
            <ReportLibrary />
          </Route>
          <Route path="/reports/:id/print">{(params) => <ReportPrint id={params.id} />}</Route>
          {/* Workbooks (C1). Registered before `/reports/:id` so the more
              specific pattern wins, as the print route already relies on. */}
          <Route path="/reports/:id/workbook/:span">
            {(params) =>
              isWorkbookSpan(params.span) ? (
                <WorkbookPage id={params.id} span={params.span} />
              ) : (
                <NotFound />
              )
            }
          </Route>
          <Route path="/reports/:id">{(params) => <ReportPage id={params.id} />}</Route>

          <Route path={routes.privacy}>
            <PrivacyPolicy />
          </Route>
          <Route path={routes.terms}>
            <TermsOfService />
          </Route>

          {/* Password recovery. `updatePassword` is the target of the emailed
              link, so it must stay reachable while signed out. */}
          <Route path={routes.resetPassword}>
            <PasswordResetRequest />
          </Route>
          <Route path={routes.updatePassword}>
            <PasswordUpdate />
          </Route>

          <Route>
            <NotFound />
          </Route>
          </Switch>
        </Shell>
      </ToastProvider>
    </AuthProvider>
  );
}

function NotFound() {
  return (
    <section className="flex flex-col gap-3">
      <h1 className="text-2xl font-semibold">404 Page Not Found</h1>
      <p className="text-foreground">This page could not be found.</p>
      <Link
        href={routes.home}
        className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        Back to start
      </Link>
    </section>
  );
}

